import { useQuery } from "@tanstack/react-query";
import type { StoreProduct } from "@baze/api-client";
import type { StoreProductCard } from "@baze/engine";
import type { PageNode } from "@baze/schema";
import { needsStoreProducts } from "@baze/engine/runtime";
import { api } from "@/lib/api";
import { qk } from "@/lib/query";

/** A product as the site would show it (drafts included, so the owner sees work in progress). */
function card(p: StoreProduct): StoreProductCard {
  const files = p.files.filter((f) => f.role === "deliverable");
  return {
    slug: p.slug,
    kind: p.kind,
    title: p.title,
    subtitle: p.subtitle || undefined,
    description: p.description || undefined,
    badge: p.badge || undefined,
    cover: p.cover || undefined,
    gallery: p.gallery,
    tiers: p.tiers.map((t) => ({ id: t.id, name: t.name, price: t.price, compareAt: t.compareAt })),
    highlights: p.highlights,
    details: p.details,
    soldOut: p.stockLimit !== null && p.soldCount >= p.stockLimit,
    includes: files.length ? [`${files.length} file / tautan`] : undefined,
  };
}

/**
 * Toko products for the editor canvas and the draft preview, fetched only when the page shows store
 * sections. `store` mirrors the live site: "Beli" when the store takes payments, WhatsApp otherwise.
 */
export function useStorePreview(siteId: string, page: Pick<PageNode, "sections"> | undefined) {
  const enabled = !!page && needsStoreProducts(page);
  const products = useQuery({ queryKey: qk.store.products(siteId), queryFn: () => api.store.products(siteId), enabled, staleTime: 30_000 });
  const account = useQuery({ queryKey: qk.store.account(siteId), queryFn: () => api.store.account(siteId), enabled, staleTime: 30_000 });
  if (!enabled) return { products: undefined, store: false };
  return {
    products: products.data?.items.filter((p) => p.status !== "archived").map(card),
    store: account.data?.account.status === "active",
  };
}
